import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { z } from 'zod';

import { Cell, GridShell } from '@/components/site/shell';
import { seoHead } from '@/lib/seo';
import { verifyNewsletterFn } from '@/server/functions/newsletter';

const searchSchema = z.object({
    userId: z.string().optional(),
    secret: z.string().optional(),
});

export const Route = createFileRoute('/newsletter_/verify')({
    validateSearch: searchSchema,
    head: () =>
        seoHead({
            title: 'אימות ניוזלטר',
            description: 'אימות הרשמה לניוזלטר אשקלון ניוז.',
            path: '/newsletter/verify',
            noIndex: true,
        }),
    component: NewsletterVerifyPage,
});

function NewsletterVerifyPage() {
    const search = Route.useSearch();
    const started = useRef(false);

    const mutation = useMutation({
        mutationFn: async () => {
            if (!search.userId || !search.secret) {
                throw { message: 'קישור האימות חסר או לא תקין.', status: 400 };
            }
            return verifyNewsletterFn({
                data: {
                    userId: search.userId,
                    secret: search.secret,
                },
            });
        },
    });

    useEffect(() => {
        if (started.current) return;
        if (!search.userId || !search.secret) return;
        started.current = true;
        mutation.mutate();
        // Run once when magic-link query params are present.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot verify
    }, [search.userId, search.secret]);

    const missing = !search.userId || !search.secret;
    const pending = !missing && (mutation.isPending || (!mutation.isSuccess && !mutation.isError));
    const errorMessage =
        mutation.error instanceof Error
            ? mutation.error.message
            : mutation.error &&
                typeof mutation.error === 'object' &&
                'message' in mutation.error
              ? String((mutation.error as { message: unknown }).message)
              : null;

    return (
        <GridShell>
            <Cell className="px-6 py-12 sm:py-16">
                <p className="text-sm text-white/55">ניוזלטר</p>
                <h1 className="mt-2 text-3xl font-normal tracking-tight text-white sm:text-4xl">
                    אימות הרשמה
                </h1>

                <div className="mt-6 max-w-lg text-sm leading-relaxed">
                    {missing && (
                        <p className="text-white/55">
                            קישור האימות חסר או לא תקין. חזרו לדף הניוזלטר
                            ונסו להירשם שוב.
                        </p>
                    )}

                    {pending && (
                        <p className="text-white/55" role="status">
                            מאמתים את הכתובת ומוסיפים לרשימת התפוצה…
                        </p>
                    )}

                    {mutation.isSuccess && (
                        <p className="text-brand" role="status">
                            {mutation.data.message}
                        </p>
                    )}

                    {errorMessage && (
                        <p className="text-red-400" role="alert">
                            {errorMessage}
                        </p>
                    )}
                </div>

                <p className="mt-8 text-sm text-white/55">
                    <Link to="/newsletter" className="hover:text-white">
                        לדף הניוזלטר
                    </Link>
                    <span aria-hidden className="mx-2 text-white/20">
                        ·
                    </span>
                    <Link to="/" className="hover:text-white">
                        דף הבית
                    </Link>
                </p>
            </Cell>
        </GridShell>
    );
}
